/* eslint-disable */
import { useState, useEffect, useRef, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Favorite as HeartIcon,
  FavoriteBorder as HeartOutlineIcon,
  LocationOn as MapPinIcon,
  Bed as BedIcon,
  Bathtub as BathIcon,
  SquareFoot as SquareIcon,
  PhotoCamera as CameraIcon,
  PlayCircleOutline as PlayCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { savedPropertyAPI } from "../services/api";
import { PROPERTY_PLACEHOLDER_IMAGE, getPropertyImage, handleImageError, convertGoogleDriveVideoUrl } from "../Constants/images";
import { useToast } from "../context/ToastContext";
import { formatPrice, formatArea } from "../Utils/formatters";
import { buildPropertyImageAlt } from "../Utils/propertyImageAlt";
import { trackEvent } from "../Utils/analytics";
import { fetchFileById } from "../Api/request/propertyDetail";
import UseAxios from "../Api/http/http";

const PropertyCard = memo(function PropertyCard({
  property,
  onViewDetails,
  isLoggedIn = false,
  user = null,
  showEditDelete = false,
  onEdit,
  onDelete,
  onUnlist,
  initialSavedState = false,
  onSaveToggle,
  disableContactButton = false,
  hideViewDetailsForDraft = false,
  showUpdateVerificationButton = false,
  onUpdateVerification,
  useUpdateAsSecondaryAction = false,
  imagePriority = false,
}) {
  const toast = useToast();
  const [isSaved, setIsSaved] = useState(initialSavedState);
  const [isLoading, setIsLoading] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isImageResolving, setIsImageResolving] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState(null);
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [videoBlobUrl, setVideoBlobUrl] = useState(null);
  const imageFallbackAttempted = useRef(false);
  const videoFallbackAttempted = useRef(false);

  // Check if the logged-in user is the property owner
  const isPropertyOwner = isLoggedIn && user && property.user_id && (parseInt(property.user_id) === parseInt(user.id));

  // Automatically disable contact button if user is the property owner
  const shouldDisableContact = disableContactButton || isPropertyOwner;

  useEffect(() => {
    setIsSaved(initialSavedState);
  }, [initialSavedState]);

  const getPropertyIdentifier = () =>
    String(
      property.property_id ||
        property.propertyId ||
        property.id ||
        ""
    );

  useEffect(() => {
    const pendingContactPropertyId = location.state?.openContactPropertyId;
    if (!isLoggedIn || !pendingContactPropertyId) return;

    if (String(pendingContactPropertyId) === getPropertyIdentifier()) {
      setShowContactDetails(true);
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [
    isLoggedIn,
    location.pathname,
    location.search,
    location.state,
    navigate,
    property.id,
    property.propertyId,
    property.property_id,
  ]);

  const handleToggleSave = async (e) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      toast.showInfo("Please login to save properties");
      return;
    }

    setIsLoading(true);

    try {
      const trackedPropertyId = String(property?.id || property?.propertyId || property?.property_id || "");
      const trackedPropertyType = String(property?.propertyType || property?.property_type || "");

      if (isSaved) {
        await savedPropertyAPI.unsaveProperty(property.id);
        setIsSaved(false);
        toast.showSuccess("Property removed from saved");
        trackEvent("remove_from_wishlist", {
          item_id: trackedPropertyId,
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        });
      } else {
        await savedPropertyAPI.saveProperty(property.id);
        setIsSaved(true);
        toast.showSuccess("Property saved successfully");
        trackEvent("add_to_wishlist", {
          item_id: trackedPropertyId,
          item_category: trackedPropertyType,
          page_path: `${location.pathname}${location.search}`,
        });
      }

      if (onSaveToggle) {
        onSaveToggle(property.id, !isSaved);
      }
    } catch (error) {
      console.error("Error toggling save:", error);
      toast.showError(error.response?.data?.message || "Failed to update saved status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactOwner = (e) => {
    e.stopPropagation();

    if (!isLoggedIn) {
      navigate("/login", {
        state: {
          from: location.pathname + location.search,
          openContactPropertyId: getPropertyIdentifier(),
        },
      });
      return;
    }

    setShowContactDetails(!showContactDetails);
  };

  // Helper to get property values - handles both camelCase and snake_case
  const getProp = (camelCaseKey, snakeCaseKey, defaultValue = '') => {
    return property[camelCaseKey] !== undefined
      ? property[camelCaseKey]
      : (property[snakeCaseKey] !== undefined ? property[snakeCaseKey] : defaultValue);
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    if (typeof value === "object") {
      if (Array.isArray(value.data)) return value.data;
      if (Array.isArray(value.items)) return value.items;
      if (Array.isArray(value.files)) return value.files;
      if (Array.isArray(value.receiptData)) return value.receiptData;
    }
    return [];
  };

  const getFirstValue = (...values) => {
    for (const value of values) {
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
    }
    return "";
  };

  const sellerType = String(getProp("sellingAs", "selling_as", "Owner")).toLowerCase();
  const isBrokerSeller = sellerType === "broker";
  const defaultSellerName = "Prakash";
  const defaultSellerPhone = "6360732460";
  const sellerName = isBrokerSeller
    ? getFirstValue(property.broker, property.broker_name, defaultSellerName)
    : getFirstValue(property.ownerName, property.owner_name, defaultSellerName);
  const sellerPhone = isBrokerSeller
    ? getFirstValue(property.brokerContact, property.broker_contact, property.broker_phone_number, defaultSellerPhone)
    : getFirstValue(property.ownerContact, property.owner_contact, property.owner_phone_number, defaultSellerPhone);
  const sellerEmail = isBrokerSeller
    ? getFirstValue(property.brokerEmail, property.broker_email_id, property.broker_email)
    : getFirstValue(property.ownerEmail, property.owner_email_id, property.owner_email);
  const cardDisplayPrice = getFirstValue(
    property.estimatedCurrentValue,
    property.estimated_current_value,
    property.expectedPrice,
    property.expected_price,
    0
  );


  const perCentPriceValue = getProp('perCentPrice', 'per_cent_price', 0);
  const perSqftPriceValue = getProp('perSqftPrice', 'per_sqft_price', 0);
  const perUnitPriceDisplay =
    perCentPriceValue && Number(perCentPriceValue) > 0
      ? `${perCentPriceValue} Lakhs/Cent`
      : perSqftPriceValue && Number(perSqftPriceValue) > 0
      ? `₹${perSqftPriceValue}/sq ft`
      : null;

  const displayPropertyType = getFirstValue(property.propertyType, property.property_type);
  const displayPropertySubType = getFirstValue(property.propertySubType, property.property_sub_type);
  const propertyTypeLabel = `${displayPropertyType || ""} ${displayPropertySubType || ""}`.toLowerCase();
  const isFlatApartmentOrVilla = /flat|apartment|villa/.test(propertyTypeLabel);
  const displayFlashcardKeyword = getFirstValue(
    property.flashcardKeyword,
    property.flashcard_keyword
  );
  const displayImageBadgeLabel =
    displayFlashcardKeyword || displayPropertySubType || displayPropertyType;
  const displayArea = getFirstValue(
    property.area,
    property.area_name,
    property.areaName,
    property.locality
  );
  const displayTaluk = getFirstValue(
    property.taluk,
    property.taluka,
    property.taluk_name,
    property.talukName
  );
  const displayAreaTaluk = [displayArea, displayTaluk].filter(Boolean).join(", ");
  const displayPrice = Number(cardDisplayPrice) > 0 ? formatPrice(cardDisplayPrice) : "";

  const formatAreaMetric = (value, suffix) => {
    if (value === null || value === undefined || value === "") return "";
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return "";
    return `${numericValue.toLocaleString()} ${suffix}`;
  };

  const formatLandExtentByCentsThreshold = (value, unitValue) => {
    if (value === null || value === undefined || value === "") return "";

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) return "";

    const normalizedUnit = String(unitValue || "").trim().toLowerCase();
    const extentInCents = normalizedUnit === "acre" || normalizedUnit === "acres"
      ? numericValue * 100
      : numericValue;

    if (extentInCents < 100) {
      return `${Number(extentInCents.toFixed(2)).toLocaleString()} Cents`;
    }

    const acresValue = extentInCents / 100;
    return `${Number(acresValue.toFixed(2)).toLocaleString()} Acres`;
  };

  const bedroomCount = Number(getProp('bedrooms', 'bedroom_count', 0)) || 0;

  const getDisplayExtentUnit = (unitValue) => {
    const normalizedUnit = String(unitValue || "").trim().toLowerCase();
    if (!normalizedUnit || normalizedUnit === "cent" || normalizedUnit === "cents") {
      return "Cents";
    }
    if (normalizedUnit === "sq ft" || normalizedUnit === "sqft") {
      return "Sqft";
    }
    if (normalizedUnit === "acre" || normalizedUnit === "acres") {
      return "Acres";
    }
    return String(unitValue).trim();
  };

  const areaMetricCandidates = [
    {
      label: "Carpet Area",
      value: formatAreaMetric(
        getFirstValue(property.carpetArea, property.carpet_area),
        "sq ft"
      ),
    },
    {
      label: "Plinth Area",
      value: formatAreaMetric(
        getFirstValue(property.plinthArea, property.plinth_area),
        "sq ft"
      ),
    },
    {
      label: "Extent",
      value: formatLandExtentByCentsThreshold(
        getFirstValue(property.extentCents, property.extent_cents, property.landExtent, property.land_extent),
        getFirstValue(property.unit, property.extentUnit, property.extent_unit)
      ),
    },
  ];

  const displayAreaMetric = isFlatApartmentOrVilla && bedroomCount > 0
    ? { label: "BHK", value: `${bedroomCount}BHK` }
    : areaMetricCandidates.find((item) => item.value);

  const toMediaUrl = (item) => {
    if (!item) return null;
    if (typeof item === "string") return convertGoogleDriveVideoUrl(item);
    return convertGoogleDriveVideoUrl(item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || null);
  };

  const isLikelyUrl = (value) => {
    if (!value || typeof value !== "string") return false;
    return (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("/") ||
      value.startsWith("data:") ||
      value.startsWith("blob:")
    );
  };

  const pickFirstNonEmptyArray = (...candidates) => {
    for (const candidate of candidates) {
      const parsed = parseArray(candidate);
      if (parsed.length > 0) return parsed;
    }
    return [];
  };

  const isVideoItem = (item) => {
    if (!item || typeof item === 'string') return false;
    if (item.mediaType === 'video') return true;
    if (item.type && typeof item.type === 'string' && item.type.startsWith('video/')) return true;
    return false;
  };

  // Filter out video items so a video first in the array doesn't block the first image
  const imageArray = parseArray(getProp('images', 'images', [])).filter(item => !isVideoItem(item));
  const propertyImageMetaArray = pickFirstNonEmptyArray(
    property.property_images,
    property.propertyImages
  ).filter(item => !isVideoItem(item));
  const videoArray = pickFirstNonEmptyArray(
    property.videos,
    property.property_videos,
    property.propertyVideos
  );
  const firstVideoUrl = toMediaUrl(videoArray[0]);
  const firstImageUrl = getPropertyImage(property, getProp);

  const getMediaFileId = (item) => {
    if (!item) return "";

    const extractFileIdFromMediaUrl = (value) => {
      if (!value || typeof value !== "string") return "";

      const queryMatch = value.match(/[?&](?:id|fileId|file_id)=([a-zA-Z0-9_-]+)/i);
      if (queryMatch?.[1]) return queryMatch[1];

      const pathMatch = value.match(/\/(?:images|videos)\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i);
      if (pathMatch?.[1]) return pathMatch[1];

      const driveMatch = value.match(/\/d\/([a-zA-Z0-9_-]+)(?:[/?#]|$)/i);
      if (driveMatch?.[1]) return driveMatch[1];

      return "";
    };

    if (typeof item === "string") {
      return isLikelyUrl(item) ? extractFileIdFromMediaUrl(item) : item;
    }

    if (typeof item !== "object") return "";

    const value =
      item.id ||
      item.fileId ||
      item.file_id ||
      item.image_id ||
      item.imageId ||
      item.video_id ||
      item.videoId ||
      item.property_image_id ||
      item.propertyImageId ||
      item.media_id ||
      item.mediaId ||
      item.drive_file_id ||
      item.driveFileId ||
      extractFileIdFromMediaUrl(item.preview || item.uploadUrl || item.gcsUrl || item.url || item.path || "");
    return value ? String(value) : "";
  };

  const firstImageFileId =
    getMediaFileId(propertyImageMetaArray[0]) ||
    getMediaFileId(imageArray[0]) ||
    getMediaFileId(property.image) ||
    (property.image_id ? String(property.image_id) : "") ||
    (property.property_image_id ? String(property.property_image_id) : "");
  const firstVideoFileId = getMediaFileId(videoArray[0]);

  const getBlobUrlByFileId = async (fileId, fallbackType) => {
    if (!fileId) return null;
    const response = await UseAxios(fetchFileById({ fileId }));
    if (!response?.data) return null;

    const blob =
      response.data instanceof Blob
        ? response.data
        : response.data instanceof ArrayBuffer
        ? new Blob([response.data], { type: fallbackType })
        : null;

    if (!blob) return null;
    return URL.createObjectURL(blob);
  };

  useEffect(() => {
    const missingImageUrl =
      !firstImageUrl || firstImageUrl === PROPERTY_PLACEHOLDER_IMAGE;
    const shouldFetchImageById = Boolean(firstImageFileId) && missingImageUrl;
    const staticImageUrl = firstImageUrl;

    setResolvedImageUrl(shouldFetchImageById ? null : staticImageUrl);
    setResolvedVideoUrl(firstVideoUrl);
    setIsImageResolving(shouldFetchImageById);
    setIsImageLoading(Boolean(!shouldFetchImageById && staticImageUrl && staticImageUrl !== PROPERTY_PLACEHOLDER_IMAGE));
    imageFallbackAttempted.current = false;
    videoFallbackAttempted.current = false;
  }, [firstImageUrl, firstVideoUrl, firstImageFileId]);

  useEffect(() => {
    let active = true;

    const loadImageById = async () => {
      const missingImageUrl =
        !firstImageUrl || firstImageUrl === PROPERTY_PLACEHOLDER_IMAGE;
      const shouldFetchById = missingImageUrl;
      if (!shouldFetchById || !firstImageFileId || imageFallbackAttempted.current) return;

      imageFallbackAttempted.current = true;
      try {
        const blobUrl = await getBlobUrlByFileId(firstImageFileId, "image/jpeg");
        if (!active || !blobUrl) {
          if (active) setIsImageResolving(false);
          return;
        }
        if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
        setImageBlobUrl(blobUrl);
        setResolvedImageUrl(blobUrl);
        setIsImageLoading(true);
        setIsImageResolving(false);
      } catch (error) {
        console.error("Image proactive fallback fetch failed", error);
        if (active) setIsImageResolving(false);
      }
    };

    const loadVideoById = async () => {
      if (firstVideoUrl || !firstVideoFileId || videoFallbackAttempted.current) return;

      videoFallbackAttempted.current = true;
      try {
        const blobUrl = await getBlobUrlByFileId(firstVideoFileId, "video/mp4");
        if (!active || !blobUrl) return;
        if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
        setVideoBlobUrl(blobUrl);
        setResolvedVideoUrl(blobUrl);
      } catch (error) {
        console.error("Video proactive fallback fetch failed", error);
      }
    };

    loadImageById();
    loadVideoById();

    return () => {
      active = false;
    };
  }, [
    firstImageUrl,
    firstImageFileId,
    firstVideoUrl,
    firstVideoFileId,
    imageBlobUrl,
    videoBlobUrl,
  ]);

  useEffect(() => {
    return () => {
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
      if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
    };
  }, [imageBlobUrl, videoBlobUrl]);

  const handleCardImageError = async (event) => {
    if (!imageFallbackAttempted.current && firstImageFileId) {
      imageFallbackAttempted.current = true;
      setResolvedImageUrl(null);
      setIsImageResolving(true);
      try {
        const blobUrl = await getBlobUrlByFileId(firstImageFileId, "image/jpeg");
        if (blobUrl) {
          if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
          setImageBlobUrl(blobUrl);
          setResolvedImageUrl(blobUrl);
          setIsImageLoading(true);
          setIsImageResolving(false);
          return;
        }
      } catch (error) {
        console.error("Image fallback fetch failed", error);
      }
      setIsImageResolving(false);
    }

    setIsImageLoading(false);
    handleImageError(event);
  };

  const handleCardVideoError = async () => {
    if (videoFallbackAttempted.current || !firstVideoFileId) return;
    videoFallbackAttempted.current = true;
    try {
      const blobUrl = await getBlobUrlByFileId(firstVideoFileId, "video/mp4");
      if (blobUrl) {
        if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
        setVideoBlobUrl(blobUrl);
        setResolvedVideoUrl(blobUrl);
      }
    } catch (error) {
      console.error("Video fallback fetch failed", error);
    }
  };
  const hasImages =
    imageArray.length > 0 ||
    propertyImageMetaArray.length > 0 ||
    Boolean(property.property_image_id);
  const hasVideo = Boolean(firstVideoUrl || firstVideoFileId || resolvedVideoUrl);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        maxWidth: "100%",
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Box
        sx={{ position: "relative", cursor: "pointer" }}
        onClick={() => !(hideViewDetailsForDraft && property.isDraft) && onViewDetails(property)}
      >
        {hasImages && isImageResolving ? (
          <Box
            sx={{
              height: 240,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "grey.100",
              color: "text.secondary",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#6b7280",
                fontSize: 12,
              }}
            >
              <CircularProgress size={24} thickness={4} sx={{ color: "#1976d2" }} />
              <Typography sx={{ fontSize: 11, color: "#9ca3af" }}>
                Loading image...
              </Typography>
            </Box>
          </Box>
        ) : hasImages ? (
          <CardMedia
            component="img"
            image={resolvedImageUrl || PROPERTY_PLACEHOLDER_IMAGE}
            alt={buildPropertyImageAlt(property, {
              variant: 'listing',
            })}
            className="property-image"
            onLoad={() => setIsImageLoading(false)}
            onError={handleCardImageError}
            loading={imagePriority ? "eager" : "lazy"}
            fetchPriority={imagePriority ? "high" : "auto"}
            decoding="async"
            referrerPolicy="no-referrer"
            sx={{
              height: 240,
              width: "100%",
              objectFit: "cover",
            }}
          />
        ) : null}

        {hasImages && !isImageResolving && isImageLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.45)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={24} thickness={4} sx={{ color: "#1976d2" }} />
          </Box>
        )}

        {!hasImages && hasVideo && (
          <Box
            component="video"
            src={resolvedVideoUrl || firstVideoUrl}
            muted
            controls
            playsInline
            preload="metadata"
            onError={handleCardVideoError}
            sx={{
              height: 240,
              width: "100%",
              objectFit: "cover",
              backgroundColor: "black",
            }}
          />
        )}

        {!hasImages && !hasVideo && (
          <Box
            sx={{
              height: 240,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "grey.100",
              color: "text.secondary",
              fontWeight: 500,
            }}
          >
            No image available
          </Box>
        )}

        {/* Featured Badge */}
        {getProp('featured', 'featured', false) && (
          <Chip
            label="Featured"
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              bgcolor: "#ff6b35",
              color: "white",
              fontWeight: 600,
            }}
          />
        )}

        {/* Edit/Delete Buttons - Show when showEditDelete is true */}
        {showEditDelete ? (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
            }}
          >
            <Tooltip title="Edit Property" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit && onEdit(property);
                }}
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.95)",
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "white",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <EditIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Property" arrow>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(property);
                }}
                size="small"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.95)",
                  boxShadow: 2,
                  "&:hover": {
                    bgcolor: "white",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            </Tooltip>
            {onUnlist && (
              <Tooltip title="Unlist Property" arrow>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnlist(property);
                  }}
                  size="small"
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.95)",
                    boxShadow: 2,
                    "&:hover": {
                      bgcolor: "white",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <VisibilityOffIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          /* Favorite Button - Only show when logged in and not in edit mode */
          isLoggedIn && (
            <IconButton
              onClick={handleToggleSave}
              disabled={isLoading}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(255, 255, 255, 0.9)",
                "&:hover": {
                  bgcolor: "white",
                },
                color: isSaved ? "error.main" : "text.secondary",
                transition: "all 0.2s ease-in-out",
              }}
              size="small"
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : isSaved ? (
                <HeartIcon fontSize="small" />
              ) : (
                <HeartOutlineIcon fontSize="small" />
              )}
            </IconButton>
          )
        )}

        {/* Image Count */}
        {imageArray.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              right: 12,
              bgcolor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <CameraIcon fontSize="small" />
            <Typography variant="caption">{imageArray.length}</Typography>
          </Box>
        )}

        {/* Video Count */}
        {videoArray.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              right: imageArray.length > 0 ? 70 : 12,
              bgcolor: "rgba(0, 0, 0, 0.7)",
              color: "white",
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <PlayCircleIcon fontSize="small" />
            <Typography variant="caption">{videoArray.length}</Typography>
          </Box>
        )}

        {/* Property Type Badge */}
        {displayImageBadgeLabel && (
          <Chip
            label={displayImageBadgeLabel}
            size="small"
            sx={{
              position: "absolute",
              bottom: 12,
              left: 12,
              bgcolor: "primary.main",
              color: "white",
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          p: 2,
          '&:last-child': {
            pb: 2
          }
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          {(displayPropertyType || displayPropertySubType) && (
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                fontSize: "1.1rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
                mr: 1,
                textAlign: "left",
                alignSelf: "flex-start",
              }}
            >
              {displayPropertyType && displayPropertySubType
                ? `${displayPropertyType} - ${displayPropertySubType}`
                : displayPropertyType || displayPropertySubType}
            </Typography>
          )}
          <Box sx={{ textAlign: "right" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "flex-end",
                gap: 0.5,
                minHeight: "1.8em",
              }}
            >
              {displayAreaMetric?.value && (
                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayAreaMetric.value}
                </Typography>
              )}
              {displayAreaMetric?.value && displayPrice && (
                <Typography
                  variant="h6"
                  color="primary.main"
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.2rem",
                    lineHeight: 1,
                    px: 0.25,
                  }}
                >
                  |
                </Typography>
              )}
              {displayPrice && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "primary.main",
                    fontSize: "1.2rem",
                    lineHeight: 1,
                  }}
                >
                  {displayPrice}
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                minHeight: "1.2em",
                visibility: perUnitPriceDisplay ? "visible" : "hidden",
              }}
            >
              {perUnitPriceDisplay || "-"}
            </Typography>
          </Box>
        </Box>

        {displayAreaTaluk && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "18px 1fr",
              columnGap: 0.75,
              alignItems: "center",
              mb: 2,
            }}
          >
            <MapPinIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography
              variant="body2"
              color="primary.main"
              sx={{
                m: 0,
                p: 0,
                width: "100%",
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                lineHeight: 1.35,
                fontSize: "1.1rem",
                fontWeight: 700,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {displayAreaTaluk}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap" }}>
          {getProp('bedrooms', 'bedroom_count', 0) > 0 && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BedIcon
                sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {getProp('bedrooms', 'bedroom_count', 0)}
              </Typography>
            </Box>
          )}
          {getProp('bathrooms', 'bathrooms', 0) > 0 && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <BathIcon
                sx={{ fontSize: 16, mr: 0.5, color: "text.secondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                {getProp('bathrooms', 'bathrooms', 0)}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box sx={{ mt: "auto" }}>
          {showUpdateVerificationButton && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => onUpdateVerification && onUpdateVerification(property)}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Fields
            </Button>
          )}

          {isPropertyOwner ? (
            useUpdateAsSecondaryAction ? (
              <Stack direction="row" spacing={1}>
                {!(hideViewDetailsForDraft && property.isDraft) && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => onViewDetails(property)}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    View Details
                  </Button>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => onUpdateVerification && onUpdateVerification(property)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Fields
                </Button>
              </Stack>
            ) : (
              // Hide View Details button for draft properties if hideViewDetailsForDraft is true
              !(hideViewDetailsForDraft && property.isDraft) && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => onViewDetails(property)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  View Details
                </Button>
              )
            )
          ) : (
            <Stack direction="row" spacing={1}>
              {/* Hide View Details button for draft properties if hideViewDetailsForDraft is true */}
              {!(hideViewDetailsForDraft && property.isDraft) && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => onViewDetails(property)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  View Details
                </Button>
              )}

              {useUpdateAsSecondaryAction ? (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => onUpdateVerification && onUpdateVerification(property)}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Fields
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleContactOwner}
                  disabled={shouldDisableContact}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                    "&.Mui-disabled": {
                      background: "#cbd5e1",
                      backgroundColor: "#cbd5e1",
                      color: "#64748b",
                      outline: "none",
                      boxShadow: "none",
                      border: "none",
                    },
                  }}
                >
                  {showContactDetails ? "Hide Contact" : "Contact"}
                </Button>
              )}
            </Stack>
          )}

          {!isPropertyOwner && !useUpdateAsSecondaryAction && (
            <Collapse in={showContactDetails}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              {!sellerName && !sellerEmail && !sellerPhone ? (
                <Typography variant="body2" color="text.secondary">
                  No contact information available
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {sellerName && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {sellerName}
                    </Typography>
                  )}
                  {sellerEmail && (
                    <Typography
                      component="a"
                      href={`mailto:${sellerEmail}`}
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {sellerEmail}
                    </Typography>
                  )}
                  {sellerPhone && (
                    <Typography
                      component="a"
                      href={`tel:+91${sellerPhone}`}
                      variant="body2"
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {`+91 ${sellerPhone}`}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          </Collapse>
          )}
        </Box>
      </CardContent>
    </Card>
  );
});

export { PropertyCard };
